/**
 * useGeofenceExceptions — REQ-090 state machine for the Admin Geofence
 * Exceptions & Overrides module (ODE-P07-T07).
 *
 * Three read surfaces + one action surface, each with its own state:
 *
 *   exceptions list (`submit` → `loading` → `ready`/`unauthorized`/`blocked`/`error`)
 *   override list   (`loadOverrides` — consult-time ACTIVE/EXPIRED/REVOKED)
 *   exception detail (`openDetail` — exception joined to override provenance)
 *   override action (`createOverride`/`revokeOverride` — POSTs the VERIFIED
 *                    ODE-P05-T04 write routes; the client never writes the
 *                    store directly, REQ-067/077)
 *
 * Nothing here computes the consult-time override state: `state`, `storedState`
 * and `overridden` come from the server projection verbatim (EXPIRED is
 * projected at consult time and never persisted).
 *
 * Traces to: ODE-PRD-REQ-049, ODE-PRD-REQ-067, ODE-PRD-REQ-076,
 *            ODE-PRD-REQ-077, ODE-PRD-REQ-090.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  type AdminGeofenceExceptionDetail,
  type AdminGeofenceExceptionPage,
  type AdminGeofenceExceptionsQuery,
  type AdminGeofenceOverridePage,
  type AdminGeofenceOverridesQuery,
  type AdminGeofenceReadResult,
  type GeofenceCheckpointId,
  type GeofenceOverrideConsultState,
  type GeofenceOverrideCreateInput,
  type GeofenceOverrideMutationResult,
} from '../../api/client.js';

/** The raw exceptions filter form (all values are strings; empties omitted). */
export interface GeofenceExceptionForm {
  readonly doNumber: string;
  readonly deliveryOrderId: string;
  readonly checkpointId: string;
  readonly driverRef: string;
  readonly vehicleRef: string;
  readonly from: string;
  readonly to: string;
  readonly limit: string;
}

export const EMPTY_GEOFENCE_EXCEPTION_FORM: GeofenceExceptionForm = {
  doNumber: '',
  deliveryOrderId: '',
  checkpointId: '',
  driverRef: '',
  vehicleRef: '',
  from: '',
  to: '',
  limit: '',
};

export type GeofenceExceptionListState =
  | { readonly kind: 'pending' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly page: AdminGeofenceExceptionPage; readonly loadingMore: boolean }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export type GeofenceOverrideListState =
  | { readonly kind: 'pending' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly page: AdminGeofenceOverridePage }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export type GeofenceExceptionDetailState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading'; readonly exceptionId: string }
  | { readonly kind: 'ready'; readonly detail: AdminGeofenceExceptionDetail }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export type GeofenceOverrideActionState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'submitting' }
  | { readonly kind: 'succeeded'; readonly outcome: string; readonly message: string }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export interface GeofenceExceptionsSeams {
  readonly list?: (
    query: AdminGeofenceExceptionsQuery,
    token: string,
  ) => Promise<AdminGeofenceReadResult<AdminGeofenceExceptionPage>>;
  readonly overrides?: (
    query: AdminGeofenceOverridesQuery,
    token: string,
  ) => Promise<AdminGeofenceReadResult<AdminGeofenceOverridePage>>;
  readonly detail?: (
    exceptionId: string,
    token: string,
  ) => Promise<AdminGeofenceReadResult<AdminGeofenceExceptionDetail>>;
  readonly create?: (
    input: GeofenceOverrideCreateInput,
    token: string,
    purposeCode: string,
  ) => Promise<GeofenceOverrideMutationResult>;
  readonly revoke?: (
    deliveryOrderId: string,
    checkpointId: GeofenceCheckpointId,
    reason: string,
    token: string,
    purposeCode: string,
  ) => Promise<GeofenceOverrideMutationResult>;
}

export interface CreateOverrideAction {
  readonly deliveryOrderId: string;
  readonly checkpointId: GeofenceCheckpointId;
  readonly reason: string;
  readonly expiresAt: string;
  readonly purposeCode: string;
}

export interface RevokeOverrideAction {
  readonly deliveryOrderId: string;
  readonly checkpointId: GeofenceCheckpointId;
  readonly reason: string;
  readonly purposeCode: string;
}

export interface UseGeofenceExceptionsOptions {
  readonly token: string | null;
  readonly seams?: GeofenceExceptionsSeams;
}

export interface UseGeofenceExceptionsResult {
  readonly form: GeofenceExceptionForm;
  readonly setForm: (patch: Partial<GeofenceExceptionForm>) => void;
  readonly submit: () => void;
  readonly reset: () => void;
  readonly loadMore: () => void;
  readonly state: GeofenceExceptionListState;
  readonly overrideFilter: GeofenceOverrideConsultState | '';
  readonly setOverrideFilter: (value: GeofenceOverrideConsultState | '') => void;
  readonly loadOverrides: () => void;
  readonly overridesState: GeofenceOverrideListState;
  readonly detailState: GeofenceExceptionDetailState;
  readonly openDetail: (exceptionId: string) => void;
  readonly closeDetail: () => void;
  readonly actionState: GeofenceOverrideActionState;
  readonly createOverride: (action: CreateOverrideAction) => void;
  readonly revokeOverride: (action: RevokeOverrideAction) => void;
  readonly clearAction: () => void;
}

/** Maps the raw form to the wire query (empties omitted, limit as a number). */
export function toExceptionQuery(form: GeofenceExceptionForm): AdminGeofenceExceptionsQuery {
  const query: Record<string, string | number> = {};
  if (form.doNumber.trim() !== '') query.doNumber = form.doNumber.trim();
  if (form.deliveryOrderId.trim() !== '') query.deliveryOrderId = form.deliveryOrderId.trim();
  if (form.checkpointId.trim() !== '') query.checkpointId = form.checkpointId.trim() as GeofenceCheckpointId;
  if (form.driverRef.trim() !== '') query.driverRef = form.driverRef.trim();
  if (form.vehicleRef.trim() !== '') query.vehicleRef = form.vehicleRef.trim();
  if (form.from.trim() !== '') query.from = form.from.trim();
  if (form.to.trim() !== '') query.to = form.to.trim();
  const limit = Number.parseInt(form.limit, 10);
  if (form.limit.trim() !== '' && Number.isInteger(limit)) query.limit = limit;
  return query as AdminGeofenceExceptionsQuery;
}

function mapListResult(
  result: AdminGeofenceReadResult<AdminGeofenceExceptionPage>,
): GeofenceExceptionListState {
  switch (result.status) {
    case 200:
      return result.payload === null
        ? { kind: 'error', message: 'Respons daftar pengecualian tidak valid.' }
        : { kind: 'ready', page: result.payload, loadingMore: false };
    case 401:
      return { kind: 'unauthorized' };
    case 403:
      return { kind: 'blocked', message: result.message ?? 'Forbidden' };
    default:
      return { kind: 'error', message: result.message ?? `HTTP ${result.status}` };
  }
}

export function useGeofenceExceptions(
  options: UseGeofenceExceptionsOptions,
): UseGeofenceExceptionsResult {
  const { token } = options;
  const seams = options.seams ?? {};

  const [form, setFormState] = useState<GeofenceExceptionForm>(EMPTY_GEOFENCE_EXCEPTION_FORM);
  const [state, setState] = useState<GeofenceExceptionListState>({ kind: 'pending' });
  const [overrideFilter, setOverrideFilterState] = useState<GeofenceOverrideConsultState | ''>('');
  const [overridesState, setOverridesState] = useState<GeofenceOverrideListState>({ kind: 'pending' });
  const [detailState, setDetailState] = useState<GeofenceExceptionDetailState>({ kind: 'idle' });
  const [actionState, setActionState] = useState<GeofenceOverrideActionState>({ kind: 'idle' });
  const [request, setRequest] = useState<{ readonly attempt: number; readonly query: AdminGeofenceExceptionsQuery } | null>(
    null,
  );
  const attemptRef = useRef(0);
  const stateRef = useRef(state);
  stateRef.current = state;

  const listRef = useRef(
    seams.list ?? ((query: AdminGeofenceExceptionsQuery, t: string) => api.listGeofenceExceptions(query, t)),
  );
  listRef.current =
    seams.list ?? ((query: AdminGeofenceExceptionsQuery, t: string) => api.listGeofenceExceptions(query, t));
  const overridesRef = useRef(
    seams.overrides ?? ((query: AdminGeofenceOverridesQuery, t: string) => api.listGeofenceOverrides(query, t)),
  );
  overridesRef.current =
    seams.overrides ?? ((query: AdminGeofenceOverridesQuery, t: string) => api.listGeofenceOverrides(query, t));
  const detailRef = useRef(
    seams.detail ?? ((id: string, t: string) => api.fetchGeofenceExceptionDetail(id, t)),
  );
  detailRef.current = seams.detail ?? ((id: string, t: string) => api.fetchGeofenceExceptionDetail(id, t));
  const createRef = useRef(
    seams.create ?? ((input: GeofenceOverrideCreateInput, t: string, p: string) => api.createGeofenceOverride(input, t, p)),
  );
  createRef.current =
    seams.create ?? ((input: GeofenceOverrideCreateInput, t: string, p: string) => api.createGeofenceOverride(input, t, p));
  const revokeRef = useRef(
    seams.revoke ??
      ((doId: string, cp: GeofenceCheckpointId, reason: string, t: string, p: string) =>
        api.revokeGeofenceOverride(doId, cp, reason, t, p)),
  );
  revokeRef.current =
    seams.revoke ??
    ((doId: string, cp: GeofenceCheckpointId, reason: string, t: string, p: string) =>
      api.revokeGeofenceOverride(doId, cp, reason, t, p));

  const setForm = useCallback((patch: Partial<GeofenceExceptionForm>) => {
    setFormState((current) => ({ ...current, ...patch }));
  }, []);

  const submit = useCallback(() => {
    attemptRef.current += 1;
    setRequest({ attempt: attemptRef.current, query: toExceptionQuery(form) });
  }, [form]);

  const reset = useCallback(() => {
    setFormState(EMPTY_GEOFENCE_EXCEPTION_FORM);
    setRequest(null);
    setState({ kind: 'pending' });
    setDetailState({ kind: 'idle' });
  }, []);

  useEffect(() => {
    if (request === null) return undefined;
    if (token === null) {
      setState({ kind: 'unauthorized' });
      return undefined;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    listRef
      .current(request.query, token)
      .then((result) => {
        if (!cancelled) setState(mapListResult(result));
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({ kind: 'error', message: error instanceof Error ? error.message : 'Network error' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, request]);

  const loadMore = useCallback(() => {
    const current = stateRef.current;
    if (current.kind !== 'ready' || !current.page.hasMore || current.page.nextCursor === null) return;
    const cursor = current.page.nextCursor;
    const baseQuery = request?.query ?? {};
    setState({ ...current, loadingMore: true });
    void (async () => {
      if (token === null) return;
      try {
        const result = await listRef.current({ ...baseQuery, cursor }, token);
        if (result.status === 200 && result.payload !== null) {
          setState((latest) => {
            if (latest.kind !== 'ready') return latest;
            return {
              kind: 'ready',
              loadingMore: false,
              page: { ...result.payload!, rows: [...latest.page.rows, ...result.payload!.rows] },
            };
          });
        } else {
          setState({ kind: 'error', message: result.message ?? `HTTP ${result.status}` });
        }
      } catch (error: unknown) {
        setState({ kind: 'error', message: error instanceof Error ? error.message : 'Network error' });
      }
    })();
  }, [request, token]);

  const loadOverrides = useCallback(() => {
    if (token === null) {
      setOverridesState({ kind: 'unauthorized' });
      return;
    }
    setOverridesState({ kind: 'loading' });
    const query: AdminGeofenceOverridesQuery = overrideFilter === '' ? {} : { state: overrideFilter };
    void overridesRef
      .current(query, token)
      .then((result) => {
        switch (result.status) {
          case 200:
            setOverridesState(
              result.payload === null
                ? { kind: 'error', message: 'Respons daftar override tidak valid.' }
                : { kind: 'ready', page: result.payload },
            );
            break;
          case 401:
            setOverridesState({ kind: 'unauthorized' });
            break;
          case 403:
            setOverridesState({ kind: 'blocked', message: result.message ?? 'Forbidden' });
            break;
          default:
            setOverridesState({ kind: 'error', message: result.message ?? `HTTP ${result.status}` });
        }
      })
      .catch((error: unknown) => {
        setOverridesState({ kind: 'error', message: error instanceof Error ? error.message : 'Network error' });
      });
  }, [overrideFilter, token]);

  const setOverrideFilter = useCallback((value: GeofenceOverrideConsultState | '') => {
    setOverrideFilterState(value);
  }, []);

  const openDetail = useCallback(
    (exceptionId: string) => {
      if (token === null) {
        setDetailState({ kind: 'unauthorized' });
        return;
      }
      setDetailState({ kind: 'loading', exceptionId });
      void detailRef
        .current(exceptionId, token)
        .then((result) => {
          if (result.status === 200 && result.payload !== null) {
            setDetailState({ kind: 'ready', detail: result.payload });
          } else if (result.status === 404) {
            setDetailState({ kind: 'not_found' });
          } else if (result.status === 401) {
            setDetailState({ kind: 'unauthorized' });
          } else if (result.status === 403) {
            setDetailState({ kind: 'blocked', message: result.message ?? 'Forbidden' });
          } else {
            setDetailState({ kind: 'error', message: result.message ?? `HTTP ${result.status}` });
          }
        })
        .catch((error: unknown) => {
          setDetailState({ kind: 'error', message: error instanceof Error ? error.message : 'Network error' });
        });
    },
    [token],
  );

  const closeDetail = useCallback(() => {
    setDetailState({ kind: 'idle' });
  }, []);

  const clearAction = useCallback(() => {
    setActionState({ kind: 'idle' });
  }, []);

  const refreshAfterMutation = useCallback(
    (exceptionId: string | null) => {
      if (request !== null) submit();
      if (exceptionId !== null) openDetail(exceptionId);
    },
    [openDetail, request, submit],
  );

  const createOverride = useCallback(
    (action: CreateOverrideAction) => {
      if (token === null) {
        setActionState({ kind: 'unauthorized' });
        return;
      }
      setActionState({ kind: 'submitting' });
      const input: GeofenceOverrideCreateInput = {
        deliveryOrderId: action.deliveryOrderId,
        checkpointId: action.checkpointId,
        reason: action.reason,
        expiresAt: action.expiresAt.trim() === '' ? null : action.expiresAt.trim(),
      };
      void createRef
        .current(input, token, action.purposeCode)
        .then((result) => {
          if (result.status === 201 || result.status === 200) {
            setActionState({
              kind: 'succeeded',
              outcome: result.outcome ?? 'created',
              message:
                result.outcome === 'replayed'
                  ? 'Override sudah ada dengan alasan yang sama (idempoten).'
                  : 'Override dibuat.',
            });
            refreshAfterMutation(detailState.kind === 'ready' ? detailState.detail.exception.exceptionId : null);
          } else if (result.status === 409) {
            setActionState({ kind: 'blocked', message: result.message ?? 'Konflik: alasan berbeda.' });
          } else if (result.status === 401) {
            setActionState({ kind: 'unauthorized' });
          } else if (result.status === 403) {
            setActionState({ kind: 'blocked', message: result.message ?? 'Forbidden' });
          } else {
            setActionState({ kind: 'error', message: result.message ?? `HTTP ${result.status}` });
          }
        })
        .catch((error: unknown) => {
          setActionState({ kind: 'error', message: error instanceof Error ? error.message : 'Network error' });
        });
    },
    [detailState, refreshAfterMutation, token],
  );

  const revokeOverride = useCallback(
    (action: RevokeOverrideAction) => {
      if (token === null) {
        setActionState({ kind: 'unauthorized' });
        return;
      }
      setActionState({ kind: 'submitting' });
      void revokeRef
        .current(action.deliveryOrderId, action.checkpointId, action.reason, token, action.purposeCode)
        .then((result) => {
          if (result.status === 200) {
            setActionState({
              kind: 'succeeded',
              outcome: result.outcome ?? 'revoked',
              message: result.outcome === 'noop' ? 'Override sudah dicabut (idempoten).' : 'Override dicabut.',
            });
            refreshAfterMutation(detailState.kind === 'ready' ? detailState.detail.exception.exceptionId : null);
          } else if (result.status === 401) {
            setActionState({ kind: 'unauthorized' });
          } else if (result.status === 403) {
            setActionState({ kind: 'blocked', message: result.message ?? 'Forbidden' });
          } else if (result.status === 404) {
            setActionState({ kind: 'error', message: result.message ?? 'Override tidak ditemukan.' });
          } else {
            setActionState({ kind: 'error', message: result.message ?? `HTTP ${result.status}` });
          }
        })
        .catch((error: unknown) => {
          setActionState({ kind: 'error', message: error instanceof Error ? error.message : 'Network error' });
        });
    },
    [detailState, refreshAfterMutation, token],
  );

  return {
    form,
    setForm,
    submit,
    reset,
    loadMore,
    state,
    overrideFilter,
    setOverrideFilter,
    loadOverrides,
    overridesState,
    detailState,
    openDetail,
    closeDetail,
    actionState,
    createOverride,
    revokeOverride,
    clearAction,
  };
}
